import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

/** Cuánto hay que arrastrar para que cuente como gesto; menos que esto es un toque. */
const DRAG_THRESHOLD_PX = 12;

export type SheetDragDirection = 'up' | 'down';

/**
 * El asa de las hojas. WCAG 2.5.7: todo lo que hace el arrastre se hace también con un toque. Es un botón
 * real, con `aria-expanded`, y las flechas del teclado equivalen a arrastrar. Interna de la lib.
 */
@Component({
  selector: 'ui-sheet-handle',
  template: `
    <button
      type="button"
      class="handle"
      [attr.aria-expanded]="expanded()"
      [attr.aria-label]="label()"
      (click)="onClick()"
      (pointerdown)="onPointerDown($event)"
      (pointerup)="onPointerUp($event)"
      (keydown)="onKeydown($event)"
    >
      <span class="grip" aria-hidden="true"></span>
    </button>
  `,
  styleUrl: './sheet-handle.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SheetHandleComponent {
  readonly expanded = input.required<boolean>();
  readonly label = input.required<string>();
  /** Un toque, o Enter y Espacio. */
  readonly tapped = output<void>();
  /** Un arrastre, o las flechas. */
  readonly dragged = output<SheetDragDirection>();

  private dragStartY: number | null = null;
  /** El navegador manda un `click` después del `pointerup`: si fue arrastre, ese clic no cuenta. */
  private wasDrag = false;

  protected onClick(): void {
    if (this.wasDrag) {
      this.wasDrag = false;
      return;
    }
    this.tapped.emit();
  }

  protected onPointerDown(event: PointerEvent): void {
    this.dragStartY = event.clientY;
    this.wasDrag = false;
  }

  protected onPointerUp(event: PointerEvent): void {
    if (this.dragStartY === null) return;
    const delta = event.clientY - this.dragStartY;
    this.dragStartY = null;
    if (Math.abs(delta) < DRAG_THRESHOLD_PX) return;
    this.wasDrag = true;
    this.dragged.emit(delta < 0 ? 'up' : 'down');
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    this.dragged.emit(event.key === 'ArrowUp' ? 'up' : 'down');
  }
}
